import { useCallback, useEffect, useMemo, useState } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { GraftonChat } from "@/components/GraftonChat";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { DocumentsManager } from "@/components/DocumentsManager";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Landmark, ExternalLink, Loader2, Sparkles, CalendarClock, ShieldCheck, FileText, Copy, Download } from "lucide-react";
import { apiJson } from "@/lib/api";
import ThinkingPulse from "@/components/ThinkingPulse";

interface Evaluation { verdict: string; raisons: string[]; infos_manquantes: string[] }
interface Aide {
  id: string; nom: string; organisme: string; type: string; plateforme: string; url_depot: string; url_source: string;
  mode_depot: string; statut_2026_10: string; echeance: string; echeance_iso: string | null; conditions: string;
  financement_officiel: string; pieces: string | string[]; confiance: string; verifie_le: string;
  evaluation: Evaluation; dossier_statut?: string | null;
}
interface Profil { forme_juridique: string | null; date_creation: string | null; region: string | null; effectif: number; depenses_rd: boolean; ca_annuel: number | null; notes: string | null }
interface Dossier {
  aide_id: string; statut: string; reference?: string; date_depot?: string; prochaine_action?: string; prochaine_action_date?: string;
  montant_demande?: number; montant_obtenu?: number; notes?: string; brouillon?: string; brouillon_date?: string; historique?: { statut: string; date: string }[];
}
interface Echeance { aide_id: string; type: string; libelle: string; date: string; jours: number | null }

const STATUTS: Record<string, string> = {
  a_etudier: "À étudier", en_preparation: "En préparation", depose: "Déposé", en_instruction: "En instruction",
  accorde: "Accordé", refuse: "Refusé", abandonne: "Abandonné",
};
const VERDICT: Record<string, { label: string; cls: string }> = {
  probable: { label: "Éligibilité probable", cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
  possible: { label: "Éligibilité possible", cls: "bg-sky-500/15 text-sky-500 border-sky-500/30" },
  a_confirmer: { label: "À confirmer", cls: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
  improbable: { label: "Peu probable", cls: "bg-muted text-muted-foreground border-border" },
};
const TYPES: Record<string, string> = { subvention: "Subvention", pret: "Prêt", credit_impot: "Crédit d'impôt", exoneration: "Exonération", garantie: "Garantie", concours: "Concours" };

const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");
const asList = (p: string | string[]) => (Array.isArray(p) ? p : String(p || "").split(/[;\n]/).map((x) => x.trim()).filter(Boolean));

export default function FinancementDashboard() {
  const [aides, setAides] = useState<Aide[]>([]);
  const [profil, setProfil] = useState<Profil | null>(null);
  const [dossiers, setDossiers] = useState<Dossier[]>([]);
  const [echeances, setEcheances] = useState<Echeance[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtre, setFiltre] = useState("pertinentes");
  const [open, setOpen] = useState<Aide | null>(null);
  const [form, setForm] = useState<Dossier | null>(null);
  const [busy, setBusy] = useState(false);
  const [draftBusy, setDraftBusy] = useState(false);
  const [profilForm, setProfilForm] = useState<Profil | null>(null);

  const load = useCallback(async () => {
    try {
      const [c, d, e] = await Promise.all([
        apiJson<{ aides: Aide[]; profil: Profil }>("/api/financement/catalogue"),
        apiJson<{ dossiers: Dossier[] }>("/api/financement/dossiers"),
        apiJson<{ echeances: Echeance[] }>("/api/financement/echeances"),
      ]);
      setAides(c.aides); setProfil(c.profil); setProfilForm(c.profil); setDossiers(d.dossiers); setEcheances(e.echeances);
    } catch {
      toast.error("Impossible de charger le catalogue d'aides.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const byId = useMemo(() => Object.fromEntries(aides.map((a) => [a.id, a])), [aides]);
  const dossierOf = (id: string) => dossiers.find((d) => d.aide_id === id);
  const visibles = aides.filter((a) => filtre === "toutes" || (filtre === "pertinentes" && a.evaluation.verdict !== "improbable") || a.evaluation.verdict === filtre);

  const ouvrir = (a: Aide) => { setOpen(a); setForm(dossierOf(a.id) || { aide_id: a.id, statut: "a_etudier" }); };

  const sauver = async () => {
    if (!open || !form) return;
    setBusy(true);
    try {
      const { aide_id, brouillon, brouillon_date, historique, ...champs } = form;
      const saved = await apiJson<Dossier>(`/api/financement/dossiers/${open.id}`, { method: "PUT", body: JSON.stringify(champs) });
      setForm(saved);
      toast.success("Suivi enregistré");
      await load();
    } catch { toast.error("Enregistrement impossible"); } finally { setBusy(false); }
  };

  const brouillon = async () => {
    if (!open) return;
    setDraftBusy(true);
    try {
      const r = await apiJson<{ brouillon: string; date: string }>(`/api/financement/dossiers/${open.id}/brouillon`, { method: "POST" });
      setForm((f) => (f ? { ...f, brouillon: r.brouillon, brouillon_date: r.date } : f));
      toast.success("Brouillon généré : à relire, rien n'est déposé");
      await load();
    } catch { toast.error("Génération impossible"); } finally { setDraftBusy(false); }
  };

  const sauverProfil = async () => {
    if (!profilForm) return;
    try {
      await apiJson("/api/financement/profil", { method: "PUT", body: JSON.stringify({
        forme_juridique: profilForm.forme_juridique, date_creation: profilForm.date_creation, region: profilForm.region,
        effectif: profilForm.effectif, depenses_rd: profilForm.depenses_rd, ca_annuel: profilForm.ca_annuel, notes: profilForm.notes,
      }) });
      toast.success("Profil enregistré : l'éligibilité est recalculée");
      await load();
    } catch { toast.error("Profil invalide (date au format AAAA-MM-JJ)"); }
  };

  const mission = () => {
    if (!open) return "";
    const p = profil;
    const pieces = asList(open.pieces);
    return [
      `MISSION COWORK : dépôt de dossier « ${open.nom} » (${open.organisme})`,
      `Plateforme : ${open.plateforme} : ${open.url_depot}`,
      `Source officielle des conditions : ${open.url_source}`,
      "",
      "1. Ouvre la plateforme. Joël se connecte lui-même (identifiants, double authentification, captcha) : attends qu'il ait fini.",
      "2. Parcours le formulaire et remplis-le UNIQUEMENT avec les informations ci-dessous. Si un champ demande une donnée absente, laisse-le vide et note-le.",
      "",
      "Informations connues sur l'entreprise :",
      `- Forme juridique : ${p?.forme_juridique || "[non renseignée]"}`,
      `- Date de création : ${p?.date_creation || "[non renseignée]"}`,
      `- Région du siège : ${p?.region || "[non renseignée]"}`,
      `- Effectif : ${p?.effectif ?? 0} salarié(s)`,
      form?.montant_demande ? `- Montant demandé : ${form.montant_demande} €` : "- Montant demandé : [à confirmer avec Joël]",
      "",
      form?.brouillon ? "Texte du dossier (brouillon relu par Joël) :\n" + form.brouillon : "Aucun brouillon : demande à Joël de générer le brouillon dans Grafton avant de remplir les champs de texte.",
      "",
      pieces.length ? "Pièces à joindre (Joël fournit les fichiers) : " + pieces.join(" ; ") : "Pièces : à vérifier sur la plateforme.",
      "",
      "3. STOP avant l'envoi final : résume à Joël ce qui est rempli et ce qui manque, puis attends sa validation explicite.",
      "4. Après le dépôt : relève la référence et la date, et demande à Joël de les saisir dans Grafton (Financement > ce dossier).",
      "",
      "Règles : ne jamais inventer une donnée ; ne cocher aucune attestation sur l'honneur sans validation de Joël ; ne rien payer ; t'arrêter si un doute apparaît.",
    ].join("\n");
  };

  const telecharger = () => {
    if (!form?.brouillon || !open) return;
    const url = URL.createObjectURL(new Blob([form.brouillon], { type: "text/plain;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = `brouillon_${open.id}.txt`; a.click(); URL.revokeObjectURL(url);
  };

  const suivis = dossiers.filter((d) => d.statut !== "a_etudier" || d.brouillon || d.notes);
  const enCours = dossiers.filter((d) => ["en_preparation", "depose", "en_instruction"].includes(d.statut)).length;
  const accordes = dossiers.filter((d) => d.statut === "accorde");

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 overflow-y-auto">
          <header className="h-14 flex items-center gap-3 border-b border-border px-4">
            <SidebarTrigger />
            <Landmark className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">Financement et aides</h1>
          </header>
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 space-y-6">
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-muted-foreground flex gap-2">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
              <p>Catalogue sourcé sur les sites officiels, vérifié le 07/10/2026. Grafton prépare le dossier, rappelle les échéances et suit l'avancement. Pour le dépôt sur la plateforme de l'organisme (Bpifrance, Région, Urssaf…), Claude Cowork peut naviguer et remplir les formulaires à partir d'une mission préparée ici : vous vous connectez et validez l'envoi final. Aucune aide n'est comptée comme du chiffre d'affaires.</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { l: "Aides au catalogue", v: String(aides.length) },
                { l: "Pertinentes pour vous", v: String(aides.filter((a) => a.evaluation.verdict !== "improbable").length) },
                { l: "Dossiers en cours", v: String(enCours) },
                { l: "Aides accordées", v: String(accordes.length) },
              ].map((k) => (
                <Card key={k.l}><CardContent className="p-4"><p className="text-2xl font-bold">{k.v}</p><p className="text-xs text-muted-foreground">{k.l}</p></CardContent></Card>
              ))}
            </div>

            <Tabs defaultValue="catalogue">
              <TabsList className="flex-wrap h-auto">
                <TabsTrigger value="catalogue">Catalogue</TabsTrigger>
                <TabsTrigger value="dossiers">Mes dossiers ({suivis.length})</TabsTrigger>
                <TabsTrigger value="echeances">Échéances</TabsTrigger>
                <TabsTrigger value="profil">Mon profil</TabsTrigger>
                <TabsTrigger value="pieces">Pièces</TabsTrigger>
              </TabsList>

              <TabsContent value="catalogue" className="space-y-4 mt-4">
                <div className="flex flex-wrap gap-2">
                  {[["pertinentes", "Pertinentes"], ["possible", "Possibles"], ["a_confirmer", "À confirmer"], ["improbable", "Peu probables"], ["toutes", "Toutes"]].map(([k, l]) => (
                    <Button key={k} size="sm" variant={filtre === k ? "default" : "outline"} onClick={() => setFiltre(k)}>{l}</Button>
                  ))}
                </div>
                {loading && <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Chargement…</p>}
                <div className="grid md:grid-cols-2 gap-4">
                  {visibles.map((a) => {
                    const v = VERDICT[a.evaluation.verdict] || VERDICT.a_confirmer;
                    return (
                      <Card key={a.id} className="flex flex-col">
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between gap-2">
                            <CardTitle className="text-base leading-snug">{a.nom}</CardTitle>
                            <Badge variant="outline" className={`shrink-0 text-[10px] ${v.cls}`}>{v.label}</Badge>
                          </div>
                          <CardDescription>{a.organisme} · {TYPES[a.type] || a.type}</CardDescription>
                        </CardHeader>
                        <CardContent className="flex-1 flex flex-col gap-2 text-sm">
                          <p><span className="text-muted-foreground">Financement : </span>{a.financement_officiel}</p>
                          <p><span className="text-muted-foreground">Statut : </span>{a.statut_2026_10} · <span className="text-muted-foreground">Échéance : </span>{a.echeance}</p>
                          {a.evaluation.raisons[0] && <p className="text-xs text-muted-foreground">{a.evaluation.raisons[0]}</p>}
                          {a.evaluation.infos_manquantes.length > 0 && <p className="text-xs text-amber-500">À renseigner dans « Mon profil » : {a.evaluation.infos_manquantes.join(", ")}.</p>}
                          <div className="mt-auto flex flex-wrap items-center gap-2 pt-2">
                            <Button size="sm" onClick={() => ouvrir(a)}>{a.dossier_statut ? `Suivi : ${STATUTS[a.dossier_statut]}` : "Ouvrir le dossier"}</Button>
                            <Button size="sm" variant="outline" asChild><a href={a.url_depot} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5 mr-1" />Plateforme</a></Button>
                            <span className="text-[10px] text-muted-foreground">Vérifié le {fmtDate(a.verifie_le)} · confiance {a.confiance}</span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="dossiers" className="space-y-3 mt-4">
                {suivis.length === 0 && <p className="text-sm text-muted-foreground">Aucun dossier suivi. Ouvrez une aide du catalogue pour démarrer son suivi.</p>}
                {suivis.map((d) => {
                  const a = byId[d.aide_id];
                  return (
                    <Card key={d.aide_id}>
                      <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="font-medium">{a?.nom || d.aide_id}</p>
                          <p className="text-xs text-muted-foreground">{a?.organisme} · Réf. {d.reference || "—"} · Déposé le {fmtDate(d.date_depot)}</p>
                          {d.prochaine_action && <p className="text-xs mt-1">Prochaine action : {d.prochaine_action} ({fmtDate(d.prochaine_action_date)})</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{STATUTS[d.statut]}</Badge>
                          {a && <Button size="sm" variant="outline" onClick={() => ouvrir(a)}>Ouvrir</Button>}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </TabsContent>

              <TabsContent value="echeances" className="space-y-3 mt-4">
                {echeances.length === 0 && <p className="text-sm text-muted-foreground">Aucune échéance datée. Renseignez la prochaine action d'un dossier pour recevoir un rappel ici.</p>}
                {echeances.map((e, i) => (
                  <Card key={i}>
                    <CardContent className="p-4 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <CalendarClock className="h-4 w-4 text-primary" />
                        <div>
                          <p className="text-sm font-medium">{byId[e.aide_id]?.nom || e.aide_id}</p>
                          <p className="text-xs text-muted-foreground">{e.libelle}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className={e.jours !== null && e.jours <= 14 ? "border-destructive/40 text-destructive" : ""}>{fmtDate(e.date)}{e.jours !== null ? ` · J${e.jours >= 0 ? "-" : "+"}${Math.abs(e.jours)}` : ""}</Badge>
                    </CardContent>
                  </Card>
                ))}
                <p className="text-xs text-muted-foreground">Les aides « au fil de l'eau » n'ont pas de date limite connue : seules les échéances vérifiées sont listées.</p>
              </TabsContent>

              <TabsContent value="profil" className="mt-4">
                {profilForm && (
                  <Card>
                    <CardHeader><CardTitle className="text-base">Faits sur l'entreprise</CardTitle><CardDescription>L'éligibilité se calcule uniquement à partir de ces données : renseignez ce que vous savez, laissez vide sinon.</CardDescription></CardHeader>
                    <CardContent className="grid md:grid-cols-2 gap-4">
                      <div><Label>Forme juridique</Label><Input value={profilForm.forme_juridique || ""} placeholder="SAS" onChange={(e) => setProfilForm({ ...profilForm, forme_juridique: e.target.value })} /></div>
                      <div><Label>Date de création (AAAA-MM-JJ)</Label><Input value={profilForm.date_creation || ""} placeholder="2026-01-15" onChange={(e) => setProfilForm({ ...profilForm, date_creation: e.target.value })} /></div>
                      <div><Label>Région du siège</Label><Input value={profilForm.region || ""} placeholder="Île-de-France" onChange={(e) => setProfilForm({ ...profilForm, region: e.target.value })} /></div>
                      <div><Label>Effectif (salariés)</Label><Input type="number" value={profilForm.effectif} onChange={(e) => setProfilForm({ ...profilForm, effectif: Number(e.target.value) })} /></div>
                      <div className="flex items-center gap-2 pt-6"><input id="rd" type="checkbox" checked={profilForm.depenses_rd} onChange={(e) => setProfilForm({ ...profilForm, depenses_rd: e.target.checked })} /><Label htmlFor="rd">Dépenses de R&D en cours (développement du produit)</Label></div>
                      <div className="md:col-span-2"><Label>Notes</Label><Textarea value={profilForm.notes || ""} onChange={(e) => setProfilForm({ ...profilForm, notes: e.target.value })} /></div>
                      <div className="md:col-span-2"><Button onClick={sauverProfil}>Enregistrer et recalculer</Button></div>
                    </CardContent>
                  </Card>
                )}
                {profil && <p className="text-xs text-muted-foreground mt-2">Aucune valeur n'est préremplie : ce que vous n'avez pas renseigné est signalé « à confirmer ».</p>}
              </TabsContent>

              <TabsContent value="pieces" className="mt-4"><DocumentsManager /></TabsContent>
            </Tabs>
          </div>
        </main>

        <Dialog open={!!open} onOpenChange={(o) => { if (!o) { setOpen(null); setForm(null); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {open && form && (
              <>
                <DialogHeader>
                  <DialogTitle>{open.nom}</DialogTitle>
                  <DialogDescription>{open.organisme} · dépôt sur {open.plateforme} · {open.mode_depot}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                  <p><span className="text-muted-foreground">Conditions : </span>{open.conditions}</p>
                  {asList(open.pieces).length > 0 && <p><span className="text-muted-foreground">Pièces habituelles : </span>{asList(open.pieces).join(" · ")}</p>}
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" asChild><a href={open.url_depot} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5 mr-1" />Ouvrir la plateforme</a></Button>
                    <Button size="sm" variant="outline" asChild><a href={open.url_source} target="_blank" rel="noreferrer"><FileText className="h-3.5 w-3.5 mr-1" />Source officielle</a></Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 md:col-span-1">
                      <Label>Statut</Label>
                      <select className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={form.statut} onChange={(e) => setForm({ ...form, statut: e.target.value })}>
                        {Object.entries(STATUTS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                      </select>
                    </div>
                    <div><Label>Référence du dossier</Label><Input value={form.reference || ""} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
                    <div><Label>Date de dépôt</Label><Input type="date" value={form.date_depot || ""} onChange={(e) => setForm({ ...form, date_depot: e.target.value })} /></div>
                    <div><Label>Montant demandé (€)</Label><Input type="number" value={form.montant_demande ?? ""} onChange={(e) => setForm({ ...form, montant_demande: e.target.value === "" ? undefined : Number(e.target.value) })} /></div>
                    <div><Label>Prochaine action</Label><Input value={form.prochaine_action || ""} onChange={(e) => setForm({ ...form, prochaine_action: e.target.value })} /></div>
                    <div><Label>Date de la prochaine action</Label><Input type="date" value={form.prochaine_action_date || ""} onChange={(e) => setForm({ ...form, prochaine_action_date: e.target.value })} /></div>
                    {form.statut === "accorde" && <div><Label>Montant obtenu (€)</Label><Input type="number" value={form.montant_obtenu ?? ""} onChange={(e) => setForm({ ...form, montant_obtenu: e.target.value === "" ? undefined : Number(e.target.value) })} /></div>}
                    <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={sauver} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer le suivi"}</Button>
                    <Button variant="outline" onClick={brouillon} disabled={draftBusy}>{draftBusy ? <ThinkingPulse size="sm" label="Grafton rédige…" /> : <><Sparkles className="h-4 w-4 mr-1" />Préparer un brouillon</>}</Button>
                  </div>
                  <div className="space-y-2 rounded-lg border border-border p-3">
                    <p className="text-xs text-muted-foreground">Dépôt assisté : copiez cette mission et donnez-la à Claude Cowork. Il ouvre la plateforme, remplit le formulaire, puis s'arrête avant l'envoi : vous validez.</p>
                    <Button size="sm" variant="outline" onClick={() => { void navigator.clipboard.writeText(mission()); toast.success("Mission Cowork copiée"); }}><Copy className="h-3.5 w-3.5 mr-1" />Copier la mission Cowork</Button>
                  </div>
                  {form.brouillon && (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">Brouillon du {fmtDate(form.brouillon_date)} : à relire, les « [À COMPLÉTER] » sont des informations que Grafton ne connaît pas. Rien n'est déposé automatiquement.</p>
                      <Textarea readOnly className="min-h-[220px] text-xs" value={form.brouillon} />
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => { void navigator.clipboard.writeText(form.brouillon || ""); toast.success("Copié"); }}><Copy className="h-3.5 w-3.5 mr-1" />Copier</Button>
                        <Button size="sm" variant="outline" onClick={telecharger}><Download className="h-3.5 w-3.5 mr-1" />Télécharger</Button>
                      </div>
                    </div>
                  )}
                  {form.historique && form.historique.length > 0 && (
                    <div><p className="text-xs text-muted-foreground mb-1">Historique</p>
                      <ul className="text-xs space-y-0.5">{form.historique.map((h, i) => <li key={i}>{fmtDate(h.date)} : {STATUTS[h.statut] || h.statut}</li>)}</ul></div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        <GraftonChat
          context={{ page: "financement", aides_pertinentes: aides.filter((a) => a.evaluation.verdict !== "improbable").map((a) => ({ nom: a.nom, verdict: a.evaluation.verdict, statut_dossier: a.dossier_statut })) }}
          subtitle="Expert en financement"
          welcomeMessage="Je m'appuie sur le catalogue vérifié. Je peux vous aider à choisir les aides pertinentes, préparer un brouillon et suivre vos dépôts."
          quickPrompts={["Quelles aides sont vraiment pertinentes pour moi ?", "Que dois-je renseigner pour affiner l'éligibilité ?", "Prépare le brouillon pour Innov'up"]}
        />
      </div>
    </SidebarProvider>
  );
}
